import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { customerService, CustomerResponse, Customer } from '../../services/customerService';
import { t } from 'i18next';

interface CustomerSearchProps {
  onSelectCustomer: (customer: { 
    id: number; 
    full_name: string; 
    phone?: string;
  }) => void;
  selectedCustomer?: { 
    id: number; 
    full_name: string;
    phone?: string;
  } | null;
  selectedPhone?: string;
  onCreateCustomer?: (phoneNumber: string) => void;
  searchResults?: Array<{
    id: number;
    full_name: string;
    phone?: string;
  }>;
  setSearchResults?: (results: Array<{
    id: number;
    full_name: string;
    phone?: string;
  }>) => void;
}

export default function CustomerSearch({ 
  onSelectCustomer, 
  selectedCustomer,
  selectedPhone,
  onCreateCustomer,
  searchResults: externalSearchResults,
  setSearchResults: setExternalSearchResults
}: CustomerSearchProps) {
  const { theme } = useTheme();
  const [searchPhoneNumber, setSearchPhoneNumber] = useState(selectedPhone || '');
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [internalSearchResults, setInternalSearchResults] = useState<Array<{
    id: number;
    full_name: string;
    phone?: string;
    external_ref?: string;
  }>>([]);
  
  // State for customer editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editExternalRef, setEditExternalRef] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const editModalRef = useRef<HTMLDivElement>(null);

  // Use external search results if provided, otherwise use internal
  const searchResults = externalSearchResults || internalSearchResults;
  const setSearchResults = setExternalSearchResults || setInternalSearchResults;

  const handlePhoneSearch = async (phoneNumber: string) => {
    if (!phoneNumber) return;
    
    setIsSearchingCustomer(true);
    setCustomerError(null);
    setSearchResults([]);
    
    try {
      const customers = await customerService.getCustomers({ search: phoneNumber });
      const customerList = Array.isArray(customers) 
        ? customers 
        : (customers as CustomerResponse).customers;
      
      if (customerList.length > 0) {
        setSearchResults(customerList);
      } else {
        setCustomerError('Not found.');
      }
    } catch (error) {
      console.error('Error searching customer:', error);
      setCustomerError('Error searching for customer');
    } finally {
      setIsSearchingCustomer(false);
    }
  };

  const handleSelectCustomer = (customer: { id: number; full_name: string; phone?: string }) => {
    onSelectCustomer(customer);
    setCustomerError('');
    setSearchResults([]);
  };

  const handleEditClick = async (e: React.MouseEvent, customer: { id: number; full_name: string; phone?: string }) => {
    e.stopPropagation(); // Prevent triggering the parent button's onClick
    try {
      // Fetch the complete customer data
      const fullCustomerData = await customerService.getCustomer(customer.id);
      setEditingCustomer(fullCustomerData);
      setEditName(fullCustomerData.full_name);
      setEditPhone(fullCustomerData.phone || '');
      setEditExternalRef(fullCustomerData.external_ref || '');
      setIsEditing(true);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  const handleSaveCustomer = async () => {
    if (!editingCustomer) return;
    
    setIsSaving(true);
    try {
      const updatedCustomer = await customerService.updateCustomer(editingCustomer.id, {
        full_name: editName,
        phone: editPhone,
        external_ref: editExternalRef
      });
      
      // Update the customer in search results
      const updatedResults = searchResults.map(customer => 
        customer.id === updatedCustomer.id 
          ? { ...customer, full_name: updatedCustomer.full_name, phone: updatedCustomer.phone }
          : customer
      );
      setSearchResults(updatedResults);
      
      // If this was the selected customer, update it
      if (selectedCustomer && selectedCustomer.id === updatedCustomer.id) {
        onSelectCustomer({
          id: updatedCustomer.id,
          full_name: updatedCustomer.full_name,
          phone: updatedCustomer.phone
        });
      }
      
      // Close the edit modal
      setIsEditing(false);
      setEditingCustomer(null);
    } catch (error) {
      console.error('Error updating customer:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingCustomer(null);
  };

  // Close modal when clicking outside
  const handleClickOutside = (e: MouseEvent) => {
    if (editModalRef.current && !editModalRef.current.contains(e.target as Node)) {
      handleCancelEdit();
    }
  };

  // Add event listener for clicking outside the modal
  useEffect(() => {
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  return (
    <div className="w-full">
      <div className="space-y-2">
        <label className={`block text-sm font-medium ${
          theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
          {t('pages.createTicket.fields.searchCustomer')}
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={searchPhoneNumber}
            onChange={(e) => {
              // Allow international phone formats: digits, +, -, spaces, parentheses, dots
              const internationalPhone = e.target.value.replace(/[^0-9+\-\s().]/g, '');
              setSearchPhoneNumber(internationalPhone);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handlePhoneSearch(searchPhoneNumber);
              }
            }}
            placeholder={t('pages.createTicket.fields.searchCustomerByPhone')}
            inputMode="tel"
            className={`
              min-w-0 flex-1 rounded-md px-3 py-2 border border-border
              shadow-sm transition-colors duration-200 ease-in-out
              focus:outline-none focus:ring-2 focus:ring-blue-500/20
              ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500 hover:border-gray-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 hover:border-gray-400'
              }
            `}
          />
          <button
            type="button"
            onClick={() => handlePhoneSearch(searchPhoneNumber)}
            disabled={isSearchingCustomer}
            className={`shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors 
              ${theme === 'dark'
                ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20'
              }
            `}
          >
            {isSearchingCustomer ? `${t('pages.createTicket.fields.searching')}` : `${t('pages.createTicket.fields.search')}`}
          </button>
        </div>
      </div>

      {searchResults.length > 0 && (
        <div className={`mt-2 p-2 rounded-md border max-w-full ${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          <div className={`text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {searchResults.length === 1 ?
            t('pages.createTicket.fields.foundCustomers', { count: searchResults.length }) :
            t('pages.createTicket.fields.foundCustomersPlural', { count: searchResults.length })
            }
          </div>
          <div className="w-full space-y-2">
            {searchResults.map((customer) => (
              <div
                key={customer.id}
                className={`w-full text-left p-3 rounded-md transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 focus:bg-gray-700'
                    : 'hover:bg-gray-50 focus:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div 
                    className="min-w-0 flex-1 cursor-pointer"
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    <div className={`font-medium truncate ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                    }`}>
                      {customer.full_name}
                    </div>
                    <div className={`text-sm truncate ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {customer.phone || searchPhoneNumber}
                    </div>
                  </div>
                  <div className="ml-2 flex shrink-0 space-x-3">
                    <button
                      type="button"
                      onClick={() => handleSelectCustomer(customer)}
                      className={`text-sm font-medium ${
                        theme === 'dark' 
                          ? 'text-indigo-400 hover:text-indigo-300' 
                          : 'text-indigo-600 hover:text-indigo-500'
                      }`}
                    >
                      {t('pages.createTicket.fields.select')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleEditClick(e, customer)}
                      className={`text-sm font-medium ${
                        theme === 'dark' 
                          ? 'text-amber-400 hover:text-amber-300' 
                          : 'text-amber-600 hover:text-amber-500'
                      }`}
                    >
                      {t('pages.createTicket.fields.edit')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {customerError && !searchResults.length && (
        <div className="flex items-center space-x-2 mt-2">
          <div className={`text-sm ${
            theme === 'dark' ? 'text-red-400' : 'text-red-600'
          }`}>
            {customerError}
          </div>
          {onCreateCustomer && (
            <button
              type="button"
              onClick={() => onCreateCustomer(searchPhoneNumber)}
              className={`text-sm font-medium ${
                theme === 'dark'
                  ? 'text-indigo-400 hover:text-indigo-300'
                  : 'text-indigo-600 hover:text-indigo-500'
              }`}
            >
              Add new?
            </button>
          )}
        </div>
      )}


{selectedCustomer && (
        <div className={`p-3 rounded-md ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
        }`}>
          <div className="font-medium">{selectedCustomer.full_name}</div>
          <div className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {selectedCustomer.phone || selectedPhone}
          </div>
        </div>
      )}

      
      {/* Edit Customer Modal */}
      {isEditing && editingCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={editModalRef}
            className={`p-6 rounded-lg shadow-xl max-w-md w-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
          >
            <h3 className={`text-lg font-medium mb-4 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
              Edit Customer
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className={`w-full rounded-md px-3 py-2 ${theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Phone
                </label>
                <input
                  type="text"
                  value={editPhone}
                  onChange={(e) => {
                    // Allow international phone formats: digits, +, -, spaces, parentheses, dots
                    const internationalPhone = e.target.value.replace(/[^0-9+\-\s().]/g, '');
                    setEditPhone(internationalPhone);
                  }}
                  inputMode="tel"
                  className={`w-full rounded-md px-3 py-2 ${theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  External Reference ID
                </label>
                <input
                  type="text"
                  value={editExternalRef}
                  onChange={(e) => setEditExternalRef(e.target.value)}
                  className={`w-full rounded-md px-3 py-2 ${theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-gray-100' 
                    : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="User ID in external system"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={handleCancelEdit}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${theme === 'dark' 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleSaveCustomer}
                  disabled={isSaving}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${theme === 'dark' 
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
